import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox, Select, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { agencyApiSelectors } from '../../../api/agency'
import { userApiEvents } from '../../../api/user'
import { TAdminUserLookup, TUserRole } from '../../../api/user/types'
import TestAgencyPill from '../TestAgencyPill/TestAgencyPill'
import { agencyDisplayName, agencyEntityLine, roleLabel } from '../../labels'
import styles from './GrantAccessPanel.module.scss'

type TProps = {
  user: TAdminUserLookup
  onClose: () => void
}

type TRoleOption = { value: TUserRole; label: string }

const ROLE_OPTIONS: TRoleOption[] = [
  { value: 'TeamMember', label: roleLabel('TeamMember') },
  { value: 'ReadOnlyMember', label: roleLabel('ReadOnlyMember') },
  { value: 'Owner', label: roleLabel('Owner') },
]

const MAX_RESULTS = 60

const GrantAccessPanel = ({ user, onClose }: TProps) => {
  const dispatch = useDispatch()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [role, setRole] = useState<TRoleOption>(ROLE_OPTIONS[0])

  const agencyListing = useSelector(agencyApiSelectors.getAgencyListing)

  // Agencies the user is already in are left out; the bulk endpoint would
  // only report them as AlreadyMember.
  const options = useMemo(() => {
    const current = new Set(user.agencies.map(a => a.agencyId))
    return (agencyListing ?? [])
      .filter(a => a.agencyId && !current.has(a.agencyId))
      .sort((a, b) => agencyDisplayName(a).localeCompare(agencyDisplayName(b)))
  }, [agencyListing, user.agencies])

  const results = useMemo(() => {
    const term = query.trim().toLowerCase()
    const matched = term
      ? options.filter(
          o =>
            (o.agencyName ?? '').toLowerCase().includes(term) ||
            (o.tradingAs ?? '').toLowerCase().includes(term) ||
            o.agencyId.includes(term),
        )
      : options
    return matched.slice(0, MAX_RESULTS)
  }, [options, query])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const toggle = (agencyId: string, checked: boolean) =>
    setSelected(prev => (checked ? [...prev, agencyId] : prev.filter(id => id !== agencyId)))

  // Closes on the request: the refresh epic re-reads the user, and the page
  // shows the per-agency outcomes.
  const submit = () => {
    dispatch(
      userApiEvents.adminGrantAccess_request({
        userId: user.userId,
        email: user.email,
        agencies: selected.map(agencyId => ({ agencyId, role: role.value })),
      }),
    )
    onClose()
  }

  return (
    <aside className={styles.drawer} aria-label="Add to agencies">
      <div className={styles.root}>
        <header className={styles.header}>
          <h2 className={styles.title}>Add to agencies</h2>
          <p className={styles.intro}>{user.email}</p>
        </header>

        <div className={styles.fields}>
          <Select<TRoleOption>
            name="role"
            label="Role in every selected agency"
            options={ROLE_OPTIONS}
            value={role}
            onChange={option => option && setRole(option)}
          />
          <TextField
            inputComponent={
              <TextInput
                type={TextFieldTypes.text}
                name="agencySearch"
                autoComplete="off"
                placeholder="Search agencies"
                value={query}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
              />
            }
          />
        </div>

        <div className={styles.results}>
          {results.length === 0 ? (
            <p className={styles.empty}>
              {options.length === 0 ? 'This user is already in every agency.' : 'No agency matches that.'}
            </p>
          ) : (
            <ul className={styles.list}>
              {results.map(option => (
                <li key={option.agencyId} className={styles.option}>
                  <Checkbox
                    name={`agency-${option.agencyId}`}
                    checked={selected.includes(option.agencyId)}
                    onChange={({ checked }: { checked: boolean }) => toggle(option.agencyId, checked)}
                    label={
                      <span className={styles['option-label']}>
                        <span className={styles['option-name']}>
                          {agencyDisplayName(option)}
                          {option.isTestAgency && <TestAgencyPill />}
                        </span>
                        {agencyEntityLine(option) && (
                          <span className={styles['option-entity']}>{agencyEntityLine(option)}</span>
                        )}
                      </span>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          {options.length > results.length && query.trim() === '' && (
            <p className={styles.empty}>Showing {MAX_RESULTS}. Search to narrow it down.</p>
          )}
        </div>

        <div className={styles.warning} role="note">
          <strong>Before you add</strong>
          <ul>
            <li>No email is sent. A plain login lands in the agency added last, so tell the user.</li>
            <li>Never add a support account: its next login becomes a session in that agency.</li>
          </ul>
        </div>

        <footer className={styles.footer}>
          <Button type="button" secondary onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={selected.length === 0} onClick={submit}>
            {selected.length > 1 ? `Add to ${selected.length} agencies` : 'Add to agency'}
          </Button>
        </footer>
      </div>
    </aside>
  )
}

export default GrantAccessPanel
